/*
 * When organic art may move.
 *
 * Exports
 *   usePrefersReducedMotion(): boolean   follows the OS setting live
 *   useDocumentVisible(): boolean        false while the tab is hidden
 *   useHtmlDir(): 'ltr' | 'rtl'          <html dir>, followed live through ONE
 *                                        MutationObserver shared by every user
 *   useSaveData(): boolean               the browser's data-saver hint, read once
 *   useInViewport(ref, rootMargin = '64px'): boolean
 *                                        one shared IntersectionObserver per
 *                                        rootMargin; true where none exists
 *   useMotionGate(ref, { extraStill = false })
 *     -> { reduced, still, paused, dir }
 *        still  = reduced || saveData || extraStill   (show the finished picture)
 *        paused = !still && !(in view && tab visible)  (hold the current frame)
 *   srOnly                               style object for visually hidden text
 *
 * Every API is feature-detected, so the hooks are safe where matchMedia,
 * IntersectionObserver or MutationObserver do not exist (old browsers, jsdom).
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

const REDUCE = '(prefers-reduced-motion: reduce)';
const noop = () => {};

function query(q) {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(q) : null;
  } catch {
    return null;
  }
}

function subscribeReduced(cb) {
  const m = query(REDUCE);
  if (!m) return noop;
  if (typeof m.addEventListener === 'function') {
    m.addEventListener('change', cb);
    return () => m.removeEventListener('change', cb);
  }
  if (typeof m.addListener === 'function') {
    m.addListener(cb);
    return () => m.removeListener(cb);
  }
  return noop;
}

const readReduced = () => !!(query(REDUCE) || {}).matches;
const serverFalse = () => false;

export function usePrefersReducedMotion() {
  return useSyncExternalStore(subscribeReduced, readReduced, serverFalse);
}

function subscribeVisible(cb) {
  if (typeof document === 'undefined') return noop;
  document.addEventListener('visibilitychange', cb);
  return () => document.removeEventListener('visibilitychange', cb);
}

const readVisible = () => typeof document === 'undefined' || !document.hidden;

export function useDocumentVisible() {
  return useSyncExternalStore(subscribeVisible, readVisible, () => true);
}

const dirListeners = new Set();
let dirObserver = null;

function subscribeDir(cb) {
  dirListeners.add(cb);
  if (!dirObserver && typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
    dirObserver = new MutationObserver(() => dirListeners.forEach((f) => f()));
    dirObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });
  }
  return () => {
    dirListeners.delete(cb);
    if (!dirListeners.size && dirObserver) {
      dirObserver.disconnect();
      dirObserver = null;
    }
  };
}

const readDir = () => (typeof document !== 'undefined'
  && document.documentElement.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr');

export function useHtmlDir() {
  return useSyncExternalStore(subscribeDir, readDir, () => 'ltr');
}

function readSaveData() {
  try {
    return typeof navigator !== 'undefined' && navigator.connection?.saveData === true;
  } catch {
    return false;
  }
}

export function useSaveData() {
  const [saveData] = useState(readSaveData);
  return saveData;
}

const observers = new Map();

function watch(el, rootMargin, fn) {
  let entry = observers.get(rootMargin);
  if (!entry) {
    const cbs = new Map();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const f = cbs.get(e.target);
        if (f) f(!!(e.isIntersecting || e.intersectionRatio > 0));
      }
    }, { rootMargin });
    entry = { io, cbs };
    observers.set(rootMargin, entry);
  }
  entry.cbs.set(el, fn);
  entry.io.observe(el);
  return () => {
    entry.cbs.delete(el);
    entry.io.unobserve(el);
    if (!entry.cbs.size) {
      entry.io.disconnect();
      if (observers.get(rootMargin) === entry) observers.delete(rootMargin);
    }
  };
}

const hasIO = () => typeof IntersectionObserver !== 'undefined';

export function useInViewport(ref, rootMargin = '64px') {
  const [inView, setInView] = useState(() => !hasIO());
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasIO()) return undefined;
    return watch(el, rootMargin, setInView);
  }, [ref, rootMargin]);
  return inView;
}

export function useMotionGate(ref, { extraStill = false } = {}) {
  const reduced = usePrefersReducedMotion();
  const saveData = useSaveData();
  const visible = useDocumentVisible();
  const inView = useInViewport(ref);
  const dir = useHtmlDir();
  const still = reduced || saveData || extraStill;
  return { reduced, still, paused: !still && !(inView && visible), dir };
}

export const srOnly = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};
